import axios from "axios";
import { END_POINT } from "../constants/defaultValues";

const baseUrl = `${END_POINT}/api/shopee/v2`;
export const SHOPEE_CLIENT_REQUEST_TIMEOUT_MS = 20000;

export const createIdempotencyKey = (scope = "shopee") =>
  `${scope}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

export const getShopeeJobIds = response => {
  const data = response && response.data;
  const candidates = data && Array.isArray(data.job_ids)
    ? data.job_ids
    : [data && data.job_id];
  return Array.from(
    new Set(candidates.filter(Boolean).map(jobId => String(jobId)))
  );
};

const unwrap = response => response.data;

const get = (path, params, signal) =>
  axios
    .get(`${baseUrl}${path}`, {
      params,
      signal,
      timeout: SHOPEE_CLIENT_REQUEST_TIMEOUT_MS
    })
    .then(unwrap);

const mutate = (method, path, data, idempotencyKey) =>
  axios({
    method,
    url: `${baseUrl}${path}`,
    data,
    timeout: SHOPEE_CLIENT_REQUEST_TIMEOUT_MS,
    headers: { "Idempotency-Key": idempotencyKey || createIdempotencyKey() }
  }).then(unwrap);

const defaultAuthorizationRedirect = () =>
  typeof window !== "undefined" &&
  (window.location.protocol === "https:" ||
    window.location.protocol === "http:")
    ? `${window.location.origin}/app/pages/ecommerce`
    : "/app/pages/ecommerce";

const authorizationRedirect = redirectUrl => {
  if (
    redirectUrl &&
    String(redirectUrl).startsWith("/") &&
    typeof window !== "undefined" &&
    (window.location.protocol === "https:" ||
      window.location.protocol === "http:")
  ) {
    return new URL(String(redirectUrl), window.location.origin).toString();
  }
  return redirectUrl || defaultAuthorizationRedirect();
};

const ShopeeV2Api = {
  shops: signal => get("/shops", null, signal),
  authorizationUrl: (redirectUrl, signal) =>
    get(
      "/shops/authorization-url",
      {
        redirect_url: authorizationRedirect(redirectUrl)
      },
      signal
    ),
  disconnectShop: (shopId, key) =>
    mutate("delete", `/shops/${shopId}`, {}, key),
  capabilities: (shopId, signal) =>
    get("/capabilities", shopId ? { shop_id: shopId } : null, signal),
  health: (shopId, signal) => get("/health", { shop_id: shopId }, signal),
  summary: (shopId, filters, signal) =>
    get("/analytics/summary", { shop_id: shopId, ...(filters || {}) }, signal),
  issues: (shopId, signal) => get("/issues", { shop_id: shopId }, signal),
  remediateIssues: (shopId, action, ids, key) =>
    mutate(
      "post",
      "/issues/remediate",
      { shop_id: shopId, action, ids },
      key
    ),
  orders: (shopId, filters, signal) =>
    get("/orders", { shop_id: shopId, ...(filters || {}) }, signal),
  order: (id, signal) => get(`/orders/${id}`, null, signal),
  orderPii: id => get(`/orders/${id}/pii`),
  mappings: (shopId, filters, signal) =>
    get("/mappings", { shop_id: shopId, ...(filters || {}) }, signal),
  catalogCandidates: (filters, signal) =>
    get("/catalog/candidates", filters || {}, signal),
  providerCatalog: (shopId, filters, signal) =>
    get(
      "/catalog/provider-items",
      { shop_id: shopId, ...(filters || {}) },
      signal
    ),
  catalogCategories: (shopId, signal) =>
    get("/catalog/categories", { shop_id: shopId, language: "vi" }, signal),
  catalogAttributes: (shopId, categoryId, signal) =>
    get(
      `/catalog/categories/${categoryId}/attributes`,
      { shop_id: shopId, language: "vi" },
      signal
    ),
  catalogBrands: (shopId, categoryId, signal) =>
    get(
      `/catalog/categories/${categoryId}/brands`,
      { shop_id: shopId, language: "vi" },
      signal
    ),
  catalogLogistics: (shopId, signal) =>
    get("/catalog/logistics", { shop_id: shopId }, signal),
  listing: (shopId, itemId, signal) =>
    get(`/listings/${itemId}`, { shop_id: shopId }, signal),
  returns: (shopId, filters, signal) =>
    get("/returns", { shop_id: shopId, ...(filters || {}) }, signal),
  settlements: (shopId, filters, signal) =>
    get("/settlements", { shop_id: shopId, ...(filters || {}) }, signal),
  jobs: (shopId, filters, signal) =>
    get("/jobs", { shop_id: shopId, ...(filters || {}) }, signal),
  job: (id, signal) => get(`/jobs/${id}`, null, signal),
  settings: (shopId, signal) => get("/settings", { shop_id: shopId }, signal),
  syncOrders: (shopId, key) => mutate("post", "/orders/sync", { shop_id: shopId }, key),
  createBill: (orderId, key) => mutate("post", `/orders/${orderId}/create-bill`, {}, key),
  importCatalog: (shopId, items, key) =>
    mutate("post", "/catalog/import", { shop_id: shopId, items }, key),
  createCatalogDraft: (shopId, itemId, categoryId, key) =>
    mutate(
      "post",
      "/catalog/drafts",
      { shop_id: shopId, item_id: itemId, category_id: categoryId || null },
      key
    ),
  updateMapping: (id, values, key) => mutate("patch", `/mappings/${id}`, values, key),
  syncInventory: (shopId, mappings, key) =>
    mutate("post", "/inventory/sync", { shop_id: shopId, mappings }, key),
  shippingParameters: (shopId, orderSn, signal) =>
    get(`/fulfillments/${orderSn}/parameters`, { shop_id: shopId }, signal),
  ship: (shopId, orderSn, payload, key) =>
    mutate("post", `/fulfillments/${orderSn}/ship`, { shop_id: shopId, ...payload }, key),
  confirmReturn: (shopId, returnSn, reason, key) =>
    mutate("post", `/returns/${returnSn}/confirm`, { shop_id: shopId, reason }, key),
  disputeReturn: (shopId, returnSn, values, key) =>
    mutate("post", `/returns/${returnSn}/dispute`, { shop_id: shopId, ...values }, key),
  syncSettlements: (shopId, orderSn, key) =>
    mutate("post", "/settlements/sync", { shop_id: shopId, order_sn: orderSn }, key),
  postIncome: (settlementId, key) =>
    mutate("post", `/settlements/${settlementId}/post-income`, {}, key),
  replayJob: (jobId, key) => mutate("post", `/jobs/${jobId}/replay`, {}, key),
  saveSettings: (values, key) => mutate("put", "/settings", values, key),
  publishListing: (values, key) => mutate("post", "/listings/publish", values, key),
  updateListing: (shopId, itemId, shopeeData, key) =>
    mutate(
      "post",
      `/listings/${itemId}/update`,
      { shop_id: shopId, shopee_data: shopeeData },
      key
    ),
  setListingStatus: (shopId, itemId, listed, key) =>
    mutate("post", `/listings/${itemId}/status`, { shop_id: shopId, listed }, key)
};

export default ShopeeV2Api;
